import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import DebtCalculator from '../../components/feature/DebtCalculator';
import ExitIntentPopup from '../../components/feature/ExitIntentPopup';

export default function ConsumerDebtSettlement() {
  useSEO({
    title: 'Consumer Debt Settlement Services | Credit Card & Personal Loan Relief | Brite Solutions',
    description: 'Eliminate credit card debt, personal loans, and medical bills with professional debt settlement. Licensed specialists negotiate 40-60% debt reduction. Free consultation, no upfront fees, A+ BBB rating.',
    keywords: 'consumer debt settlement, credit card debt relief, personal loan settlement, medical bill negotiation, debt reduction, credit card debt help, unsecured debt relief',
    canonical: '/consumer-debt-settlement',
    structuredData: {
      '@type': 'Service',
      name: 'Consumer Debt Settlement',
      provider: {
        '@type': 'Organization',
        name: 'Brite Solutions',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '1247'
        }
      },
      description: 'Professional consumer debt settlement services for credit card debt, personal loans, and medical bills',
      areaServed: 'United States',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free consultation with no upfront fees'
      }
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <ExitIntentPopup />

      {/* Enhanced SEO Meta Tags */}
      <title>Consumer Debt Settlement Services - Eliminate Credit Card Debt | Brite Solutions</title>
      <meta name="description" content="Professional consumer debt settlement services. Eliminate credit card debt, medical bills, and personal loans with up to 60% savings. Free consultation available. A+ BBB rated debt relief company serving nationwide." />
      <meta name="keywords" content="consumer debt settlement, credit card debt help, eliminate debt, debt relief services, personal debt settlement" />
      <link rel="canonical" href="https://britesolutions.com/consumer-debt-settlement" />
      
      {/* Local SEO */}
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />
      
      {/* Consumer Debt Settlement Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Consumer Debt Settlement Services",
          "description": "Professional debt settlement services for consumers struggling with credit card debt, medical bills, and personal loans.",
          "provider": {
            "@type": "FinancialService",
            "name": "Brite Solutions"
          },
          "areaServed": "US",
          "serviceType": "Debt Settlement"
        })}
      </script>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-teal-600 to-blue-600 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Consumer Debt Settlement Services
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Eliminate credit card debt, personal loans, and medical bills with our proven debt settlement program. Save 40-60% on average with licensed specialists.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => document.querySelector<HTMLElement>('#vapi-widget-floating-button')?.click()}
                className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get Free Consultation
              </button>
              <a
                href="tel:+18005551234"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-teal-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">$500M+</div>
              <div className="text-gray-600">Debt Settled</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">1,247+</div>
              <div className="text-gray-600">Satisfied Clients</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">40-60%</div>
              <div className="text-gray-600">Average Savings</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">A+</div>
              <div className="text-gray-600">BBB Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Settle */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Types of Consumer Debt We Settle
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our licensed specialists can help you settle various types of unsecured consumer debt
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-teal-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-bank-card-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Credit Card Debt</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                High-interest credit card balances from Visa, Mastercard, American Express, Discover, and store cards.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Major credit cards</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Store credit cards</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Charge cards</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-hand-coin-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Personal Loans</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Unsecured personal loans from banks, credit unions, and online lenders.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                  <span>Bank personal loans</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                  <span>Peer-to-peer loans</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                  <span>Signature loans</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-purple-600 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-hospital-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Medical Bills</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Hospital bills, doctor visits, emergency room charges, and other medical expenses.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                  <span>Hospital bills</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                  <span>Doctor bills</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-purple-600 mr-2 mt-1"></i>
                  <span>Emergency room charges</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How Consumer Debt Settlement Works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our proven 4-step process helps you become debt-free faster
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Free Consultation</h3>
              <p className="text-gray-700">
                We review your debts, income, and financial goals to create a personalized plan.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Build Savings</h3>
              <p className="text-gray-700">
                You make affordable monthly deposits into a dedicated account you control.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">We Negotiate</h3>
              <p className="text-gray-700">
                Our specialists negotiate with creditors to reduce your debt by 40-60% on average.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold text-white">
                4
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Become Debt-Free</h3>
              <p className="text-gray-700">
                Once settled, you're debt-free and can start rebuilding your financial future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <DebtCalculator />

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose Brite Solutions for Consumer Debt Settlement?
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                We've been helping consumers eliminate debt since 2008 with proven strategies and personalized service.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Licensed & Accredited</h3>
                    <p className="text-gray-700">A+ BBB rating and fully licensed debt settlement specialists.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Upfront Fees</h3>
                    <p className="text-gray-700">You only pay when we successfully settle your debt.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-user-heart-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Personalized Service</h3>
                    <p className="text-gray-700">Dedicated debt specialist guides you through every step.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-line-chart-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Proven Results</h3>
                    <p className="text-gray-700">Over $500 million settled for 1,247+ satisfied clients.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/4558e78f8cd9af1e903482fe729379ad.jpg"
                alt="Debt settlement consultation"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-teal-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Eliminate Your Consumer Debt?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get a free consultation with a licensed debt specialist today. No obligation, 100% confidential.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => document.querySelector<HTMLElement>('#vapi-widget-floating-button')?.click()}
              className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Free Consultation
            </button>
            <a
              href="tel:+18005551234"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-teal-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
